import React, { useMemo, useState, useRef } from 'react';
import { Modal } from '@components/molecules/Modal';
import { Button } from '@components/atoms/Button';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import { BillOfMaterialsTable } from './BillOfMaterialsTable';
import { generateBillOfMaterialsPdfFilename } from '@utils/pdfFilename';
import { useTranslation } from '@hooks/useTranslation';

type InventoryItem = {
  totalPrice?: number | null;
  unitPrice?: number | null;
  totalCost?: number | null;
  unitCost?: number | null;
  isOwnItem?: boolean;
  isSubcontracted?: boolean;
  originalOrganizationName?: string;
  chargeToClient?: boolean;
  amount?: number;
  name?: string;
  equipmentId?: string;
  materialId?: string;
  time?: string;
  taskName?: string;
  taskId?: string;
  unassigned?: boolean;
  displayOrganizationName?: string;
  costPerHour?: number | null;
  amountType?: string;
  source?: 'WAREHOUSE' | 'MANUAL';
};

interface BillOfMaterialsModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectName: string;
  inventory: InventoryItem[];
}

type ViewMode = 'full' | 'detailed';

export const BillOfMaterialsModal: React.FC<BillOfMaterialsModalProps> = ({
  isOpen,
  onClose,
  projectName,
  inventory,
}) => {
  const tProjectComponents = useTranslation('projectComponents');
  const t = tProjectComponents.billOfMaterials;
  const [viewMode, setViewMode] = useState<ViewMode>('full');
  const printRef = useRef<HTMLDivElement>(null);

  // Process inventory - trust backend-calculated totalCost
  const processedInventory = useMemo(() => {
    return inventory.filter((item) => {
      const hasValidTotalPrice =
        item.totalPrice !== null &&
        item.totalPrice !== undefined &&
        !isNaN(item.totalPrice);
      const hasValidTotalCost =
        item.totalCost !== null &&
        item.totalCost !== undefined &&
        !isNaN(item.totalCost);
      return hasValidTotalPrice || hasValidTotalCost;
    });
  }, [inventory]);

  // Group inventory by task for detailed view
  const groupedByTask = useMemo(() => {
    const groups: Record<string, { taskName: string; items: InventoryItem[] }> =
      {};

    processedInventory.forEach((item) => {
      const taskKey = item.taskId || item.taskName || 'Unassigned';
      const taskName = item.taskName || 'Unassigned';

      if (!groups[taskKey]) {
        groups[taskKey] = {
          taskName,
          items: [],
        };
      }
      groups[taskKey].items.push(item);
    });

    return Object.values(groups).sort((a, b) =>
      a.taskName.localeCompare(b.taskName),
    );
  }, [processedInventory]);

  // Calculate totals
  const totals = useMemo(() => {
    const totalCost = processedInventory.reduce(
      (sum, item) => sum + (item.totalCost || 0),
      0,
    );
    const totalItems = processedInventory.length;
    const totalMaterials = processedInventory.filter(
      (item) => !item.equipmentId,
    ).length;
    const totalEquipment = processedInventory.filter(
      (item) => item.equipmentId,
    ).length;

    return { totalCost, totalItems, totalMaterials, totalEquipment };
  }, [processedInventory]);

  const handlePrint = () => {
    const originalTitle = document.title;
    const pdfFilename = generateBillOfMaterialsPdfFilename(projectName, viewMode);

    // Browser usa document.title como nombre sugerido del PDF
    document.title = pdfFilename.replace('.pdf', '');

    window.print();

    // Restaurar título después del diálogo de impresión
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t.title}
      size="full"
      closeOnOverlayClick
      confirmText={t.close}
      handleConfirm={onClose}>
      <div className="p-4 bill-of-materials-modal">
        {/* Header with controls - hidden in print */}
        <div className="flex items-center justify-between mb-4 print:hidden">
          <div className="flex items-center gap-2">
            {/* View Mode Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button type="button"
                onClick={() => setViewMode('full')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'full'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}>
                {t.fullProject}
              </button>
              <button type="button"
                onClick={() => setViewMode('detailed')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'detailed'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}>
                {t.detailedByTask}
              </button>
            </div>
          </div>

          <Button variant="secondary" onClick={handlePrint}>
            <PrintOutlinedIcon sx={{ fontSize: '16px' }} className="mr-2" />
            {t.printExport}
          </Button>
        </div>

        {/* Printable Content */}
        <div ref={printRef} className="bill-of-materials-print-area">
          {/* Print Header - only visible in print */}
          <div className="hidden print:block print:mb-6">
            <h1 className="text-2xl font-bold">{projectName}</h1>
            <h2 className="text-lg text-gray-600">Bill of Materials</h2>
            <p className="text-sm text-gray-500">
              {t.generated.replace(
                '{date}',
                new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                }),
              )}
            </p>
            <p className="text-sm text-gray-500">
              {t.view.replace(
                '{mode}',
                viewMode === 'full' ? t.fullProject : t.detailedByTask,
              )}
            </p>
          </div>

          {/* Table Content */}
          <BillOfMaterialsTable
            viewMode={viewMode}
            inventory={processedInventory}
            groupedByTask={groupedByTask}
          />

          {/* Summary Footer */}
          <div className="mt-4 p-4 bg-gray-50 rounded-md grid grid-cols-2 md:grid-cols-4 gap-4 print:bg-white print:border print:border-gray-300">
            <div className="flex flex-col">
              <p className="text-sm text-gray-600">{t.totalItems}</p>
              <p className="text-lg font-bold">{totals.totalItems}</p>
            </div>
            <div className="flex flex-col">
              <p className="text-sm text-gray-600">{t.materials}</p>
              <p className="text-lg font-bold">{totals.totalMaterials}</p>
            </div>
            <div className="flex flex-col">
              <p className="text-sm text-gray-600">{t.equipment}</p>
              <p className="text-lg font-bold">{totals.totalEquipment}</p>
            </div>
            <div className="flex flex-col">
              <p className="text-sm text-gray-600">{t.totalCost}</p>
              <p className="text-lg font-bold">
                ${totals.totalCost.toLocaleString('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default BillOfMaterialsModal;
